<template>
  <div class="modal-card" style="min-height: 560px">
    <header class="modal-card-head">
      <p class="modal-card-title">
        {{ $t('pages.admin_panel.powerbi_access.title') }}
      </p>
    </header>
    <section class="modal-card-body">
      <div class="mb-4 has-text-grey">
        {{ $t('pages.admin_panel.powerbi_access.help') }}
      </div>

      <b-field :label="$t('pages.admin_panel.powerbi_access.report')">
        <multiselect
          v-model="selectedPowerBi"
          :options="powerBis"
          :loading="isLoadingPowerBis"
          :disabled="isLoadingPowerBis"
          label="name"
          track-by="id"
          :close-on-select="true"
          :placeholder="$t('pages.admin_panel.powerbi_access.select_report')"
          @input="loadedPowerBi = null"
        />
      </b-field>

      <b-button
        type="is-info"
        icon-left="users"
        :loading="isLoadingGroups"
        :disabled="!selectedPowerBi"
        @click="loadGroups"
      >
        {{ $t('pages.admin_panel.powerbi_access.load_groups') }}
      </b-button>

      <template v-if="loadedPowerBi">
        <hr>
        <p class="mb-3">
          <strong>{{ loadedPowerBi.name }}</strong>
          <span class="has-text-grey">
            — {{ $t('pages.admin_panel.powerbi_access.groups_found', { count: groups.length }) }}
          </span>
        </p>
        <b-table
          :data="groups"
          :paginated="groups.length > 15"
          :per-page="15"
          default-sort="name"
          hoverable
          narrowed
          striped
        >
          <b-table-column
            v-slot="props"
            field="id"
            label="Id"
            width="80"
            sortable
            numeric
          >
            {{ props.row.id }}
          </b-table-column>
          <b-table-column
            v-slot="props"
            field="name"
            :label="$t('pages.admin_panel.powerbi_access.group')"
            sortable
          >
            {{ props.row.name }}
          </b-table-column>
          <b-table-column
            v-slot="props"
            field="type"
            :label="$t('pages.admin_panel.powerbi_access.type')"
            sortable
          >
            <b-tag :type="typeTag(props.row.type)">
              {{ props.row.type }}
            </b-tag>
          </b-table-column>
          <b-table-column v-slot="props" :label="$t('pages.admin_panel.powerbi_access.roles')">
            <template v-if="props.row.roleNames.length">
              <b-tag v-for="role in props.row.roleNames" :key="role" class="mr-1">
                {{ role }}
              </b-tag>
            </template>
            <span v-else class="has-text-grey-light">
              {{ $t('pages.admin_panel.powerbi_access.no_role') }}
            </span>
          </b-table-column>

          <template #empty>
            <div class="has-text-centered has-text-grey p-4">
              {{ $t('pages.admin_panel.powerbi_access.no_group') }}
            </div>
          </template>
        </b-table>
      </template>
    </section>
    <footer class="modal-card-foot">
      <button class="button" @click="$emit('close')">
        {{ $t('global.close') }}
      </button>
    </footer>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import Powerbi from '@/services/v2/powerbi'

export default {
  name: 'PowerBiAccessModal',
  data () {
    return {
      powerBis: [],
      selectedPowerBi: null,
      loadedPowerBi: null,
      groups: [],
      isLoadingPowerBis: false,
      isLoadingGroups: false
    }
  },
  // The modal is rendered on first open only (destroy-on-hide is false on the
  // parent b-modal), so reports are fetched once, when the admin needs them.
  mounted: async function () {
    await this.loadPowerBis()
  },
  methods: {
    loadPowerBis: async function () {
      try {
        this.isLoadingPowerBis = true
        const reports = await Powerbi.getPowerBIReports()
        this.powerBis = (reports.value || [])
          .map(report => ({ id: report.id, name: report.name.replaceAll('_', ' ') }))
          .sort((a, b) => a.name.localeCompare(b.name))
      } catch (error) {
        this.$buefy.toast.open({ message: error.message || error, type: 'is-danger' })
      }
      this.isLoadingPowerBis = false
    },
    loadGroups: async function () {
      const powerBi = this.selectedPowerBi
      try {
        this.isLoadingGroups = true
        const [roles, groups] = await Promise.all([
          this.getRoles(powerBi.id),
          this.getGroups()
        ])

        this.groups = groups
          .map(group => {
            const access = (group.powerBis || []).find(bi => bi.id === powerBi.id)
            if (!access) return null
            return {
              ...group,
              roleNames: (access.roles || []).map(roleId => {
                const role = roles.find(el => String(el.id) === String(roleId))
                return role ? role.role : roleId
              })
            }
          })
          .filter(Boolean)
        this.loadedPowerBi = powerBi
      } catch (error) {
        this.$buefy.toast.open({ message: error.message || error, type: 'is-danger' })
      }
      this.isLoadingGroups = false
    },
    // Role names are a nicety: a failure here must not hide the groups.
    getRoles: async function (powerBiId) {
      try {
        return await Powerbi.getRoles(powerBiId)
      } catch {
        return []
      }
    },
    getGroups: async function () {
      const builder = this.$gql.query({
        operation: 'groups',
        fields: [
          'totalCount',
          { edges: [{ node: ['id', 'name', 'type', { powerBis: ['id', 'roles'] }] }] }
        ]
      })
      const result = await this.$apollo.query({
        fetchPolicy: 'no-cache',
        query: gql`${builder.query}`,
        variables: builder.variables
      })
      return result.data.groups.edges.map(e => e.node)
    },
    typeTag: function (type) {
      switch (type) {
        case 'HUB':
          return 'is-success'
        case 'CUSTOMER':
          return 'is-info'
        case 'END USER':
          return 'is-purple'
        default:
          return 'is-light'
      }
    }
  }
}
</script>
